import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes (Direction, 16/10/2026 ; avant : 10)
// Activité partagée entre les onglets (16/10/2026) : un onglet oublié ne déconnecte plus celui où l'on travaille.
const CLE_ACTIVITE = 'dmo-derniere-activite';
const CLE_DECONNEXION = 'dmo-deconnexion';
const lireStockage = (cle) => { try { return localStorage.getItem(cle); } catch { return null; } };
const ecrireStockage = (cle, v) => { try { localStorage.setItem(cle, v); } catch { /* sans stockage */ } };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur s'est produite. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [motifDeconnexion, setMotifDeconnexion] = useState('');   // 'inactivite' | 'expiree' | ''
  const lastActivityRef = useRef(Date.now());

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inactivité : 10 min sans activité dans AUCUN onglet de l'application → déconnexion (message à la connexion)
  useEffect(() => {
    if (!user || user === false) return;
    let dernierEcrit = 0;
    const bump = () => {
      const t = Date.now();
      lastActivityRef.current = t;
      if (t - dernierEcrit > 15000) { dernierEcrit = t; ecrireStockage(CLE_ACTIVITE, String(t)); }
    };
    bump();
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'visibilitychange'];
    events.forEach(ev => window.addEventListener(ev, bump, { passive: true }));

    const interval = setInterval(() => {
      const partagee = Number(lireStockage(CLE_ACTIVITE)) || 0;
      if (Date.now() - Math.max(lastActivityRef.current, partagee) > INACTIVITY_MS) {
        logout('inactivite');
      }
    }, 30 * 1000); // contrôle toutes les 30 s

    return () => {
      events.forEach(ev => window.removeEventListener(ev, bump));
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Déconnexion faite dans un autre onglet, ou session expirée (signalée par axiosSetup) : retour à la connexion avec un message
  useEffect(() => {
    const autreOnglet = (e) => {
      if (e.key !== CLE_DECONNEXION || !e.newValue) return;
      setMotifDeconnexion(e.newValue.split('|')[1] || 'expiree');
      setUser(false);
    };
    const expiree = () => { setMotifDeconnexion((m) => m || 'expiree'); setUser(false); };
    window.addEventListener('storage', autreOnglet);
    window.addEventListener('dmo-session-expiree', expiree);
    return () => { window.removeEventListener('storage', autreOnglet); window.removeEventListener('dmo-session-expiree', expiree); };
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      setMotifDeconnexion('');
      lastActivityRef.current = Date.now();
      ecrireStockage(CLE_ACTIVITE, String(Date.now()));
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const register = async (email, password, name, role) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password, name, role },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const logout = async (motif = '') => {
    const m = typeof motif === 'string' ? motif : '';
    if (m) setMotifDeconnexion(m);
    ecrireStockage(CLE_DECONNEXION, `${Date.now()}|${m || 'manuelle'}`);   // prévient les autres onglets
    try {
      await axios.post(
        `${API_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Erreur de déconnexion:', error);
    } finally {
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, register, checkAuth, motifDeconnexion }}>
      {children}
    </AuthContext.Provider>
  );
};